import { useEffect, useMemo, useRef } from 'react';
import { cloneDeep, isEmpty, isEqual, isMatch, last, set } from 'lodash-es';
import {
    BehaviorSubject,
    distinctUntilChanged,
    filter,
    map,
    type Observable,
    ReplaySubject,
} from 'rxjs';
import { useObservableState, useSubscription } from 'observable-hooks';
import warning from './logger';

export enum PLACEHOLDER_ROW_STRATEGY {
    ADD_WHEN_EMPTY = 1,
    ADD_WHEN_LAST_ROW_IS_DIRTY = 2,
}

function unknownPlaceholderStrategy(strategy: PLACEHOLDER_ROW_STRATEGY | undefined): void {
    warning(`RowManager cannot handle placeholder strategy: ${strategy}.`);
}

type EmptyRow<T extends RowType> = Omit<T, 'key'>;
type RowType = Record<string, unknown> & { key: string };

interface RowManagerConfig<Row extends RowType> {
    maxRows: number;
    emptyRow: EmptyRow<Row>;
    placeholderRowStrategy?: PLACEHOLDER_ROW_STRATEGY;
}

export class RowManager<Row extends RowType> implements RowManagerConfig<Row> {
    declare maxRows: number;

    declare emptyRow: EmptyRow<Row>;

    declare placeholderRowStrategy: PLACEHOLDER_ROW_STRATEGY;

    readonly input$: BehaviorSubject<Row[]>;

    readonly output$: ReplaySubject<Row[]>;

    readonly rows$: BehaviorSubject<Row[]>;

    readonly rowLimitReached$: Observable<boolean>;

    readonly showDeleteButtons$: Observable<boolean>;

    constructor(config: RowManagerConfig<Row>) {
        this.storeConfig(config);

        // Rows that are coming from the API.
        this.input$ = new BehaviorSubject<Row[]>([]);

        // Rows that are going back to the API.
        this.output$ = new ReplaySubject<Row[]>(1);

        // The rows that will be shown in the UI.
        this.rows$ = new BehaviorSubject<Row[]>([]);

        this.rowLimitReached$ = this.rows$.pipe(
            map((rows) => rows.length >= this.maxRows),
            distinctUntilChanged()
        );

        this.showDeleteButtons$ = this.rows$.pipe(
            map((rows) => rows.length > 1),
            distinctUntilChanged()
        );

        // Handle incoming rows.
        this.handleInputRows(this.input$).subscribe(this.rows$);

        // Handle outgoing rows.
        this.handleOutputRows(this.rows$).subscribe(this.output$);
    }

    storeConfig(config: RowManagerConfig<Row>): void {
        this.maxRows = config.maxRows;
        this.emptyRow = config.emptyRow;
        this.placeholderRowStrategy =
            config.placeholderRowStrategy ?? PLACEHOLDER_ROW_STRATEGY.ADD_WHEN_EMPTY;
    }

    // Method to handle incoming rows.
    handleInputRows(input$: Observable<Row[]>): Observable<Row[]> {
        return input$.pipe(map((incomingRows) => this.addPlaceholderRow(incomingRows)));
    }

    // Method to handle outgoing rows.
    handleOutputRows(rows$: Observable<Row[]>): Observable<Row[]> {
        return rows$.pipe(
            // Optionally strip the placeholder row that was added in `handleInputRows`.
            map((output) => this.stripPlaceholderRow(output)),
            // There is no need to emit output when the output is the same a the input.
            filter((output) => !isEqual(output, this.input$.getValue()))
        );
    }

    // Allow subclasses to override if needed; keep signature compatible with historic calls.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    createRow(_rows?: Row[]): Row {
        return this.emptyRow as Row;
    }

    addPlaceholderRow(rows: Row[]): Row[] {
        let shouldAddPlaceholderRow = false;

        switch (this.placeholderRowStrategy) {
            case PLACEHOLDER_ROW_STRATEGY.ADD_WHEN_EMPTY:
                shouldAddPlaceholderRow = isEmpty(rows);
                break;
            case PLACEHOLDER_ROW_STRATEGY.ADD_WHEN_LAST_ROW_IS_DIRTY:
                // Add a placeholder row when the last row in rows is not an
                // empty row. In other words, the last row is dirty.
                shouldAddPlaceholderRow = !isEqual(last(rows), this.emptyRow);
                break;
            default:
                unknownPlaceholderStrategy(this.placeholderRowStrategy);
        }

        return shouldAddPlaceholderRow ? [...rows, this.createRow(rows)] : rows;
    }

    isEmptyRow(row: Row): boolean {
        return isEqual(row, this.emptyRow);
    }

    stripPlaceholderRow(rows: Row[]): Row[] {
        switch (this.placeholderRowStrategy) {
            case PLACEHOLDER_ROW_STRATEGY.ADD_WHEN_EMPTY:
                if (rows.length === 1 && isMatch(rows[0], this.emptyRow)) {
                    return [];
                }
                break;
            case PLACEHOLDER_ROW_STRATEGY.ADD_WHEN_LAST_ROW_IS_DIRTY:
                return rows.filter((row) => !this.isEmptyRow(row));
            default:
                unknownPlaceholderStrategy(this.placeholderRowStrategy);
        }

        return rows;
    }

    // Add an empty row to the list.
    addRow(): void {
        const current = this.rows$.getValue();
        this.rows$.next([...current, this.createRow(current)]);
    }

    // Remove a row from the list.
    removeRow(index: number): void {
        const current = this.rows$.value;
        this.rows$.next(current.filter((_row, i) => i !== index));
    }

    /**
     * Changes a specific row in the list.
     *
     * @param index - The index of the row to change.
     * @param keyOrUpdateObject -
     *        If a string, it represents the key to update in the row.
     *        If an object, it represents an object containing key-value pairs to update.
     * @param newValue - The new value to set at the specified key.
     */
    changeRow<T extends keyof Row>(
        index: number,
        keyOrUpdateObject: T | Partial<Row> | null,
        newValue?: Row[T]
    ): void {
        const current = this.rows$.getValue();
        const copy = [...current];

        copy[index] = cloneDeep(current[index]);

        if (typeof keyOrUpdateObject === 'string') {
            // Set the new value at the specified path on the row.
            set(copy[index], keyOrUpdateObject, newValue);
        } else if (typeof keyOrUpdateObject === 'object' && keyOrUpdateObject !== null) {
            // Merge the updateObject onto the row.
            Object.assign(copy[index], keyOrUpdateObject);
        } else {
            unknownPlaceholderStrategy(this.placeholderRowStrategy);
        }

        this.rows$.next(copy);
    }

    // Method to integrate this class with React components.
    static useRowManager<R extends RowType>(
        value: R[],
        onChange: (rows: R[]) => void,
        maxRows: number,
        emptyRow: R,
        config?: Partial<RowManagerConfig<R>>
    ) {
        // ESlint will complain about that hooks cannot be called in
        // a class component, but we are not doing that.
        /* eslint-disable react-hooks/rules-of-hooks */
        const emptyRowRef = useRef(emptyRow);
        const configRef = useRef(config);
        const manager = useMemo(
            () =>
                new this({
                    maxRows,
                    emptyRow: emptyRowRef.current,
                    ...(configRef.current ?? {}),
                }),
            [maxRows, emptyRowRef, configRef]
        );

        // Push the incoming values to the RowManager.
        useEffect(() => {
            value && manager.input$.next(value);
        }, [manager, value]);

        // Push the outgoing values from RowManager to the onChange callback.
        useSubscription(manager.output$, onChange);

        const rows = useObservableState(manager.rows$);
        const rowLimitReached = useObservableState(manager.rowLimitReached$);
        const showDeleteButtons = useObservableState(manager.showDeleteButtons$);

        return { rows, manager, rowLimitReached, showDeleteButtons };
        /* eslint-enable */
    }
}
